/**
 * router/routes/rrhh.routes.ts
 * RRHH (NOMI) module routes, nested under the tenant AppShell.
 * Split out of router/index.ts (openspec/arquitectura/convencion-modulo.md §4).
 */
import type { RouteRecordRaw } from 'vue-router';

export const rrhhRoutes: RouteRecordRaw[] = [
  // RRHH shell: /rrhh — NOMI module landing.
  // meta.requiresCapability: 'nomi.legajo.leer' (rediseno-panel-roles
  // follow-up, 2026-09-15 user report — bug found testing locx.supervisor):
  // this route used to carry no guard at all ("visible to any user entitled
  // to 'nomi'"), which was already stale by the time it was written —
  // locx-supervisor-catalogo-nav's NOMI-26.1 gave the NAV MANIFEST'S
  // `nomi.home` item this exact requiredCapability so the sidebar link
  // disappears for a `locx.busqueda.solicitar`-only session, but never
  // added the matching guard to the actual Vue ROUTE — so that session
  // could still reach `/rrhh` directly (e.g. the recruitment detail view's
  // hardcoded "Volver", see RecruitmentRequestDetailView.vue) and get a raw
  // backend 403 ("This endpoint requires the 'nomi.legajo.leer'
  // capability.") from RrhhHomeView.vue's useNomiDashboard() fetch instead
  // of a clean bounce. The persons routes below keep their own guards.
  {
    path: 'rrhh',
    name: 'rrhh-home',
    component: () => import('../../views/rrhh/RrhhHomeView.vue'),
    meta: { requiresCapability: 'nomi.legajo.leer' },
  },

  // NOMI-16: /rrhh/persons — person list (nomi:read required by backend).
  {
    path: 'rrhh/persons',
    name: 'persons',
    component: () => import('../../views/rrhh/PersonListView.vue'),
    meta: { requiresCapability: 'nomi.legajo.leer' },
  },

  // Alta link inbox (2026-10-13): what people sent through an alta link, waiting for RRHH.
  {
    path: 'rrhh/alta-requests',
    name: 'alta-requests',
    component: () => import('../../views/rrhh/AltaRequestsView.vue'),
    meta: { requiresCapability: 'nomi.legajo.editar' },
  },

  // NOMI-16.1 / NOMI-17.1: /rrhh/persons/new — alta wizard (Person → Employment,
  // nomi:write required by backend). Must be defined BEFORE 'rrhh/persons/:id'.
  {
    path: 'rrhh/persons/new',
    name: 'person-new',
    component: () => import('../../views/rrhh/PersonCreateView.vue'),
    meta: { requiresCapability: 'nomi.legajo.editar' },
  },

  // NOMI-16.3: /rrhh/persons/:id — person detail + employment history (nomi:read).
  {
    path: 'rrhh/persons/:id',
    name: 'person-detail',
    component: () => import('../../views/rrhh/PersonDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'nomi.legajo.leer', remountOnParams: true },
  },

  // add-nomi-candidate-pipeline (Etapa 2 Fase 1) — solicitudes de reclutamiento
  // (nomi:recruit-request, para encargados de sector) y candidatos (nomi:recruit,
  // para RRHH). Dos capabilities distintas a propósito, ver design.md §1.
  {
    path: 'rrhh/recruitment-requests',
    name: 'recruitment-requests',
    component: () => import('../../views/rrhh/RecruitmentRequestsListView.vue'),
    meta: { requiresCapability: 'nomi.busqueda.solicitar' },
  },

  // Candidatos lives inside Reclutamiento (tabs "Búsquedas | Candidatos", 2026-10-12):
  // its paths sit under /rrhh/recruitment-requests so the sidebar keeps "Reclutamiento"
  // active. Static segments outrank `:id`, so they never collide with a search's detail.
  // The former /rrhh/candidates URLs redirect.
  {
    path: 'rrhh/recruitment-requests/candidates',
    name: 'candidates',
    component: () => import('../../views/rrhh/CandidatesListView.vue'),
    meta: { requiresCapability: 'nomi.busqueda.gestionar' },
  },

  // "Solicitantes": jefes who ask for búsquedas through a link, no account (2026-10-13).
  {
    path: 'rrhh/recruitment-requests/requesters',
    name: 'recruitment-requesters',
    component: () => import('../../views/rrhh/RecruitmentRequestersView.vue'),
    meta: { requiresCapability: 'nomi.busqueda.gestionar' },
  },

  {
    path: 'rrhh/recruitment-requests/candidates/:id',
    name: 'candidate-detail',
    component: () => import('../../views/rrhh/CandidateDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'nomi.busqueda.gestionar' },
  },

  {
    path: 'rrhh/recruitment-requests/:id',
    name: 'recruitment-request-detail',
    component: () => import('../../views/rrhh/RecruitmentRequestDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'nomi.busqueda.solicitar' },
  },

  { path: 'rrhh/candidates', redirect: { name: 'candidates' } },
  { path: 'rrhh/candidates/:id', redirect: (to) => ({ name: 'candidate-detail', params: { id: to.params['id'] } }) },

  // NOMI-26.4: /rrhh/shift-patterns/:id — weekday configuration (WEEKLY patterns).
  // The list itself is now a tab inside /rrhh/catalogs (ShiftPatternManager.vue) —
  // /rrhh/shift-patterns and /rrhh/work-sites as standalone list pages were
  // retired (same "parametric config belongs in Catálogos" fix as admin-nomi-
  // catalogs). This detail sub-page stays a real route — a full weekly-schedule
  // editor doesn't fit as a catalog row.
  {
    path: 'rrhh/shift-patterns/:id',
    name: 'shift-pattern-detail',
    component: () => import('../../views/rrhh/ShiftPatternDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'nomi.legajo.leer' },
  },

  // /rrhh/attendance-events — the "Novedades" workspace (2026-10-12): KPIs of
  // the day + planilla de dotación over each employee's shift plan, with the
  // former "Informe de novedades" list (23 jul 2026) as its second tab.
  {
    path: 'rrhh/attendance-events',
    name: 'attendance-events-report',
    component: () => import('../../views/rrhh/NovedadesView.vue'),
    meta: { requiresCapability: 'nomi.legajo.leer' },
  },

  // /rrhh/organigrama — interactive org chart (2026-10-12): by people or by
  // structure, open positions from recruitment, today's absences.
  {
    path: 'rrhh/organigrama',
    name: 'org-chart',
    component: () => import('../../views/rrhh/OrganigramaView.vue'),
    meta: { requiresCapability: 'nomi.legajo.leer' },
  },
  // operator-document-prefetch (2026-11-01): who has their documents on the phone for the gate.
  {
    path: 'rrhh/documentos-telefono',
    name: 'documents-offline',
    component: () => import('../../views/rrhh/DocumentosTelefonoView.vue'),
    meta: { requiresCapability: 'nomi.legajo.leer' },
  },
  // odd/tasks/nomi-epp-firma.md S3: EPP compliance board, register deliveries, constancia.
  {
    path: 'rrhh/epp',
    name: 'nomi-epp',
    component: () => import('../../views/rrhh/NomiEppView.vue'),
    meta: { requiresCapability: 'nomi.legajo.leer' },
  },

  // nomi-autoservicio-empleado Tier A self-service portal (design.md D12)
  // moved to apps/operator (app.rigx.sofistic.io) 2026-09-14 — architecture
  // correction: employee self-service belongs in the field-operator app,
  // not this admin app. See apps/operator/src/router/index.ts for the
  // 4 routes (documentos/organigrama/calendario/ausencias) — same
  // `/rrhh/self/*` backend, untouched.
  // /rrhh/catalogs — department/sector/position/convention/employee_category/
  // termination_reason ABM (add-nomi-domain Backlog gap, closed post Fase 2).
  // add-nomi-catalog-sidebar (16 jul 2026): sidebar NO LONGER hidden here —
  // SidebarNav's "Administración" accordion now IS the primary way in/around
  // this page (user feedback: going through the topbar and back just to
  // switch between Personas and a catálogo was "muy engorroso"). The page's
  // own in-content CatalogSidebarNav was removed to avoid a redundant second
  // sidebar (see NomiCatalogsView.vue).
  {
    path: 'rrhh/catalogs',
    name: 'nomi-catalogs',
    component: () => import('../../views/rrhh/NomiCatalogsView.vue'),
    meta: { requiresCapability: 'nomi.catalogo.editar' },
  },
];
